/**
 * Is this event target somewhere the keyboard is typing into? Duck-typed on
 * `tagName` and `isContentEditable` rather than `instanceof HTMLElement`, so
 * it loads under `node --test` (test/typing.test.ts) and answers the same for
 * an element from another frame.
 *
 * One answer for every window-level key handler on the desk. Desk Browse had
 * its own copy and desk Year had none, so Escape in a Year pane's field
 * closed the pane around it (UI-audit a4-05).
 */
export function isTypingTarget(t: unknown): boolean {
  if (!t || typeof t !== 'object') return false
  const el = t as { tagName?: unknown; isContentEditable?: unknown }
  if (el.isContentEditable === true) return true
  const tag = typeof el.tagName === 'string' ? el.tagName.toUpperCase() : ''
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

/**
 * Is this key someone else's? A window-level "Escape closes the pane" handler
 * on a desk surface asks this first: a field's Escape is the field's (above),
 * and anything inside the Ask panel or an open dialog belongs to that panel —
 * Escape typed in the Ask composer closed the Year pane beside it (UI-audit
 * a3-09), and Escape on Ask's Threads sheet would have closed both. Duck-typed
 * on `closest` for the reason `isTypingTarget` is duck-typed on `tagName`.
 */
export function isKeyElsewhere(t: unknown): boolean {
  if (isTypingTarget(t)) return true
  const el = t as { closest?: unknown } | null
  if (!el || typeof el !== 'object' || typeof el.closest !== 'function') return false
  return !!(el.closest as (s: string) => unknown).call(el, '.desk-ask, [role="dialog"]')
}
